// apps/web/src/components/Notifications.tsx

import { useSocket } from '@/hooks/useSocket';
import { useState, useEffect } from 'react';
import { trpc } from '@/trpc/client';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Bell, CheckCheck } from 'lucide-react';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string | Date;
  link?: string | null;
}

export const Notifications = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Fetch notifications - LONGER interval for slower networks
  const { data: notifications, isLoading, refetch } = trpc.notification.getMyNotifications.useQuery(
    undefined,
    {
      refetchInterval: open ? 30000 : false, // 30 seconds when open, no auto-refresh when closed
      refetchIntervalInBackground: false, // Don't refresh in background
      refetchOnWindowFocus: false, // Don't refetch on window focus (causes spam)
      staleTime: 30000, // Consider data fresh for 30 seconds
    }
  );

  const markAsReadMutation = trpc.notification.markAsRead.useMutation({
    onSuccess: () => {
      refetch();
    },
  });

  const markAllReadMutation = trpc.notification.markAllAsRead.useMutation({
    onSuccess: () => {
      refetch();
      toast.success('All notifications marked as read', { duration: 2000 });
    },
    onError: (err) => toast.error(err.message, { duration: 3000 }),
  });

  // Real-time updates
  const { socket } = useSocket();

  useEffect(() => {
    if (!socket) return;

    const handleNewNotification = () => {
      console.log('🔔 Real-time notification received!');
      refetch();
      toast.info('New notification received');
    };

    socket.on('new_notification', handleNewNotification);

    return () => {
      socket.off('new_notification', handleNewNotification);
    };
  }, [socket, refetch]);

  const unreadCount = notifications?.filter((n: Notification) => !n.isRead).length || 0;

  // Get notification emoji based on type
  const getNotificationIcon = (type: string) => {
    const icons: Record<string, string> = {
      JOB_ACCEPTED: '🎉',
      JOB_STATUS_UPDATED: '📦',
      JOB_COMPLETED: '✅',
      PAYMENT_RELEASED: '💰',
      NEW_JOB_AVAILABLE: '🚚',
      BOOKING_CONFIRMED: '✓',
      PAYMENT_RECEIVED: '💳',
      ACCOUNT_VERIFIED: '✓',
      WELCOME: '👋',
    };
    return icons[type] || '🔔';
  };

  const handleNotificationClick = (notif: Notification) => {
    if (!notif.isRead) {
      markAsReadMutation.mutate({ notificationId: notif.id });
    }

    if (notif.link) {
      navigate(notif.link);
      setOpen(false);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAllReadMutation.mutate()}
              disabled={markAllReadMutation.isPending}
              className="h-auto py-1 px-2 text-xs"
            >
              <CheckCheck className="w-3 h-3 mr-1" />
              Mark all read
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <ScrollArea className="h-[300px]">
          {isLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Loading...
            </div>
          ) : notifications && notifications.length > 0 ? (
            notifications.map((notif: Notification) => (
              <DropdownMenuItem
                key={notif.id}
                className={`cursor-pointer p-3 ${!notif.isRead ? 'bg-accent/50' : ''}`}
                onClick={() => handleNotificationClick(notif)}
              >
                <div className="flex gap-3 w-full">
                  <div className="text-2xl flex-shrink-0">
                    {getNotificationIcon(notif.type)}
                  </div>
                  <div className="flex-1 space-y-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-sm leading-tight">
                        {notif.title}
                      </p>
                      {!notif.isRead && (
                        <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {notif.message}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(notif.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              </DropdownMenuItem>
            ))
          ) : (
            <div className="p-8 text-center">
              <Bell className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">No notifications yet</p>
            </div>
          )}
        </ScrollArea>
        {notifications && notifications.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer justify-center text-primary"
              onClick={() => {
                navigate('/dashboard');
                setOpen(false);
              }}
            >
              View all notifications
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};